import { useEffect, useState } from 'react';

type FunctionElement = () => JSX.Element | null;
type CompletionCallback = () => void;

function LazyRender({
    resolvers,
    onResolved,
}: {
    resolvers: (() => Promise<FunctionElement>)[];
    onResolved?: CompletionCallback | undefined;
}): JSX.Element {
    const [views, setViews] = useState<FunctionElement[]>([]);

    useEffect(() => {
        const startNextJob = (index: number) => {
            if (index >= resolvers.length) {
                onResolved?.();
                return; /* DONE */
            }

            const callback = () => {
                /* We checked the index against the array length above so `resolver` here is guaranteed to exist */
                const resolver = resolvers[index] as () => Promise<FunctionElement>;
                resolver()
                    .then(element => {
                        setViews(currentViews => [...currentViews, element]);
                        startNextJob(index + 1);
                    })
                    .catch(error => {
                        throw error;
                    });
            };

            /* RequestIdleCallback isn't supported on Safari so just immediately call the callback in that case */
            const hasIdleCallback =
                typeof window !== 'undefined' && typeof window.requestIdleCallback !== 'undefined';

            if (hasIdleCallback) {
                requestIdleCallback(callback);
            } else {
                callback();
            }
        };

        startNextJob(0);
    }, [resolvers, onResolved]);

    return (
        <>
            {views.map((View, index) => (
                <View key={index} />
            ))}
        </>
    );
}

export default LazyRender;
